/**
 * Wraps `BroadcastChannel('evse3-tabs')` with a typed event shape so the rest
 * of the codebase can post / subscribe without juggling raw JSON.
 *
 * The channel is injectable so tests can substitute an in-process bus.
 */
import type { StationId } from '@/domain/types';

export const TAB_BROADCAST_CHANNEL = 'evse3-tabs';

export type TabBroadcastEvent =
  | { type: 'LOCK_ACQUIRED'; cpid: string; wsUrl: string; tabId: string }
  | { type: 'LOCK_RELEASED'; cpid: string; wsUrl: string; tabId: string }
  | { type: 'LOCK_STOLEN'; cpid: string; wsUrl: string; oldTabId: string; newTabId: string }
  | { type: 'STATION_UPDATED'; stationId: StationId };

export interface ChannelLike {
  postMessage(value: unknown): void;
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  removeEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  close(): void;
}

export type TabBroadcastListener = (event: TabBroadcastEvent) => void;

export class TabBroadcast {
  readonly #channel: ChannelLike;
  readonly #listeners = new Set<TabBroadcastListener>();
  readonly #raw = (event: MessageEvent): void => {
    const data = event.data as TabBroadcastEvent | undefined;
    if (!data || typeof data !== 'object' || typeof data.type !== 'string') return;
    for (const l of this.#listeners) l(data);
  };

  constructor(channel?: ChannelLike) {
    this.#channel = channel ?? createBrowserChannel();
    this.#channel.addEventListener('message', this.#raw);
  }

  post(event: TabBroadcastEvent): void {
    this.#channel.postMessage(event);
  }

  on(listener: TabBroadcastListener): () => void {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  close(): void {
    this.#channel.removeEventListener('message', this.#raw);
    this.#channel.close();
    this.#listeners.clear();
  }
}

function createBrowserChannel(): ChannelLike {
  if (typeof globalThis === 'undefined' || !('BroadcastChannel' in globalThis)) {
    throw new Error('BroadcastChannel is not available — pass a channel explicitly');
  }
  const Ctor = (globalThis as { BroadcastChannel: new (name: string) => ChannelLike })
    .BroadcastChannel;
  return new Ctor(TAB_BROADCAST_CHANNEL);
}
